/**
 * Localization helpers for the donation "Historique" tab.
 *
 * Audit rows arrive with the raw OPDON property name in `field` (e.g.
 * "TotalAmount") and the raw new value in `value` (e.g. the enum name
 * "Validated"). Rendering those verbatim breaks R29 in a fr/nl UI, so the tab
 * maps the field name to an i18n label and resolves enum values against the
 * catalog option lists used elsewhere in the module.
 */
/**
 * Audit `field` (OPDON Donation C# property name) → i18n key for the "Champ"
 * column. Keys are reused from the donation edit form / create modal so a field
 * reads identically in the history and in the form. Unmapped fields fall back to
 * the raw property name.
 */
export const HISTORY_FIELD_LABEL_KEYS = {
    TotalAmount: "finance.donations.fields.totalAmount",
    FeeAmount: "finance.donations.fields.feeAmount",
    PaymentMethod: "finance.donations.fields.paymentMethod",
    PaymentDate: "finance.donations.detail.paymentDate",
    DonationDate: "finance.donations.fields.donationDate",
    DonationNature: "finance.donations.fields.donationNature",
    DonationForm: "finance.donations.fields.donationForm",
    DonationSource: "finance.donations.fields.donationSource",
    TransactionStatus: "finance.donations.detail.statusLabel",
    ChequeNumber: "finance.donations.fields.chequeNumber",
    TaxReceiptType: "finance.donations.fields.taxReceiptType",
    Note: "finance.donations.detail.note",
    CampaignId: "finance.donations.fields.campaignLinked",
    // The OPDON audit query resolves the DonorId GUID to the donor display name before it reaches the
    // UI (OP-5822); the label keeps the row business-friendly ("Donateur : Jean Dupont").
    DonorId: "finance.donations.fields.donorContact",
    DonationType: "finance.donations.fields.donationType",
};
/**
 * Localized label for an audit field name. Falls back to the raw property name
 * for an unmapped field or a missing translation so the cell is never blank.
 */
export function historyFieldLabel(t, field) {
    const key = HISTORY_FIELD_LABEL_KEYS[field];
    if (!key)
        return field;
    const label = t(key);
    return label === key ? field : label;
}
/**
 * Localized label for a lifecycle-event row (Created / Deleted on any entity type,
 * or any operation on DonationComment / TaxReceipt). Derives the i18n key from
 * `finance.donations.detail.history.action.<entityType>.<operation>`. Falls back to
 * `"<entityType> <operation>"` when the key is not found so the cell is never blank.
 */
export function historyActionLabel(t, entityType, operation) {
    const key = `finance.donations.detail.history.action.${entityType}.${operation}`;
    const label = t(key);
    return label === key ? `${entityType} ${operation}` : label;
}
/**
 * Localized label for a catalog-backed code (`donationSource`, `paymentMethod`, …), resolved
 * against the option list `useDonationOptions` exposes for that field.
 *
 * An unknown code falls back to the code itself — better a raw value than a blank cell when a
 * tenant adds a catalog entry the UI has never seen. An absent code yields `fallback`, which
 * callers pick per surface: a table cell wants an em dash, an inline sentence wants "" so the
 * segment can be dropped rather than rendered empty.
 */
export function catalogLabel(options, code, fallback = "–") {
    if (!code)
        return fallback;
    return options.find((o) => o.value === code)?.label ?? code;
}
/** Audit fields holding an amount, which OPDON records as an invariant decimal ("101.0"). */
const HISTORY_AMOUNT_FIELDS = new Set(["TotalAmount", "FeeAmount"]);
/**
 * Audit date fields, by what they hold. The donation date is a calendar day that OPDON anchors at
 * a UTC instant, so its UTC day is the day (the donation read mapper keeps it the same way); the
 * payment date is the moment the payment was processed, so it is shown in local time.
 */
const HISTORY_DAY_FIELDS = new Set(["DonationDate"]);
const HISTORY_INSTANT_FIELDS = new Set(["PaymentDate"]);
// OPDON stringifies a DateTime with the invariant culture's general pattern ("10/19/2026 09:30:00"),
// from a UTC value (timestamptz column).
const INVARIANT_DATE_TIME = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2})$/;
const ISO_DATE = /^(\d{4}-\d{2}-\d{2})(T.*)?$/;
/**
 * An audited date value as its UTC calendar day and ISO UTC instant, or null when it is in neither
 * the invariant pattern OPDON records nor ISO.
 */
export function historyDateValue(value) {
    const invariant = INVARIANT_DATE_TIME.exec(value);
    if (invariant) {
        const day = `${invariant[3]}-${invariant[1]}-${invariant[2]}`;
        return { day, instant: `${day}T${invariant[4]}Z` };
    }
    const iso = ISO_DATE.exec(value);
    if (!iso)
        return null;
    return { day: iso[1], instant: iso[2] ? value : `${iso[1]}T00:00:00Z` };
}
/**
 * Localized label for an audit value. Enum-typed fields are resolved against the
 * catalog option list supplied for that field; amount fields go through
 * `formatAmount` and date fields through `formatDate` when the caller supplies them;
 * every other field (free text) is returned verbatim.
 */
export function historyValueLabel(field, value, optionsByField, formatAmount, formatDate) {
    if (!value)
        return value;
    if (formatAmount && HISTORY_AMOUNT_FIELDS.has(field)) {
        const amount = Number(value);
        return Number.isFinite(amount) ? formatAmount(amount) : value;
    }
    if (formatDate && (HISTORY_DAY_FIELDS.has(field) || HISTORY_INSTANT_FIELDS.has(field))) {
        const date = historyDateValue(value);
        if (!date)
            return value;
        return HISTORY_DAY_FIELDS.has(field) ? formatDate.day(date.day) : formatDate.instant(date.instant);
    }
    const options = optionsByField[field];
    if (!options)
        return value;
    // Verbatim fallback, not the em dash: a non-enum field's raw value IS the label here.
    return catalogLabel(options, value, value);
}
